import React, { useEffect } from "react";
import SiteHeader from "../components/SiteHeader";
import SiteFooter from "../components/SiteFooter";

const FONT_BODY = { fontFamily: `"SF Pro Text", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif` };

// Only three text styles allowed in the body of this page:
const H1_CLS = "text-4xl md:text-5xl font-bold tracking-tight leading-[1.1] text-[#1D1D1F]";
const H2_CLS = "text-[22px] md:text-[24px] font-bold tracking-tight text-[#1D1D1F]";
const BODY_CLS = "text-[17px] leading-[1.6] text-[#1D1D1F]";

// Stable footer crumbs (module-level so React sees the same reference on every render)
const FOOTER_CRUMBS = [{ label: "Areas We Serve" }];

const MIAMI_DADE_CITIES = [
  "Miami", "Miami Beach", "Coral Gables", "Key Biscayne",
  "Bay Harbor Islands", "Surfside", "Bal Harbour",
  "North Miami", "North Miami Beach", "Sunny Isles Beach", "Aventura",
];
const MIAMI_NEIGHBORHOODS = ["South Beach", "Brickell", "Coconut Grove"];
const BROWARD_CITIES = [
  "Hallandale Beach", "Hollywood", "Fort Lauderdale",
  "Lauderdale-by-the-Sea", "Pompano Beach", "Deerfield Beach",
];

const TITLE = "Areas We Serve | PropHero — Condo & HOA Management in Miami-Dade & Broward";
const DESC = "PropHero provides CAM-licensed condominium, HOA, and community association management across Miami-Dade County and Broward County, Florida — including Miami Beach, Aventura, Fort Lauderdale, Hollywood, and more.";
const CANONICAL = "https://prophero.io/areas-we-serve";

function setMeta(name, content, attr = "name") {
  let el = document.head.querySelector(`meta[${attr}="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function AreasWeServe() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const prevTitle = document.title;
    document.title = TITLE;
    setMeta("description", DESC);
    setCanonical(CANONICAL);
    setMeta("og:title", TITLE, "property");
    setMeta("og:description", DESC, "property");
    setMeta("og:url", CANONICAL, "property");
    setMeta("twitter:title", TITLE);
    setMeta("twitter:description", DESC);
    return () => {
      document.title = prevTitle;
      setCanonical("https://prophero.io/");
    };
  }, []);

  const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://prophero.io/" },
      { "@type": "ListItem", position: 2, name: "Areas We Serve", item: CANONICAL },
    ],
  };
  // Safely serialize JSON-LD for inline <script> — escape any `</` that could
  // close the tag early. The payload is 100% static/controlled (no user input),
  // so this is defense in depth; DOMPurify is not appropriate for JSON-LD.
  const breadcrumbJsonLdStr = JSON.stringify(breadcrumbJsonLd).replace(/</g, "\\u003c");

  return (
    <main data-testid="areas-page" className="min-h-screen bg-white text-[#0A1428]" style={FONT_BODY}>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbJsonLdStr }} />

      {/* Main site header (Apple-style, NOT sticky — scrolls away) */}
      <SiteHeader />

      {/* Section title bar — sticky under viewport top; aligned under PropHero logo */}
      <div className="sticky top-0 z-40 border-b border-black/5 bg-[#F5F5F7]/85 backdrop-blur-xl">
        <div
          className="max-w-7xl mx-auto px-6 lg:px-10 py-2.5 min-h-[51px] flex items-center text-[16px] leading-[20px] font-medium text-[#1D1D1F]"
          style={{ fontFamily: `"SF Pro Display", -apple-system, BlinkMacSystemFont, "Helvetica Neue", Arial, sans-serif`, letterSpacing: "-0.01em" }}
        >
          Areas We Serve
        </div>
      </div>

      <article className="max-w-[720px] mx-auto px-6 md:px-10 py-14 md:py-20">
        <header className="mb-10">
          <h1 className={H1_CLS}>
            Areas we serve
          </h1>
          <p className={`mt-5 ${BODY_CLS}`}>
            PropHero is a CAM-licensed property management company serving condominium associations, HOAs,
            and community associations throughout <strong>Miami-Dade County</strong> and <strong>Broward County</strong>, Florida.
            Our team handles day-to-day operations, transparent financial reporting, vendor coordination,
            board communication, and long-term capital planning for the buildings and communities across South Florida.
          </p>
        </header>

        <section className="mb-12" aria-labelledby="miami-dade">
          <h2 id="miami-dade" className={H2_CLS}>Miami-Dade County</h2>
          <p className={`mt-3 ${BODY_CLS}`}>
            We manage condominium and HOA communities across the following Miami-Dade municipalities:
          </p>
          <ul className={`mt-4 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 ${BODY_CLS}`}>
            {MIAMI_DADE_CITIES.map((c) => (
              <li key={c} className="flex items-center gap-2">
                <span aria-hidden className="w-1 h-1 rounded-full bg-[#1D1D1F]" />
                {c}
              </li>
            ))}
          </ul>

          <h2 className={`mt-8 ${H2_CLS}`}>Miami neighborhoods and districts</h2>
          <p className={`mt-2 ${BODY_CLS}`}>
            We also serve residential communities in prominent areas throughout Miami-Dade County:
          </p>
          <ul className={`mt-3 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 ${BODY_CLS}`}>
            {MIAMI_NEIGHBORHOODS.map((n) => (
              <li key={n} className="flex items-center gap-2">
                <span aria-hidden className="w-1 h-1 rounded-full bg-[#1D1D1F]" />
                {n}
              </li>
            ))}
          </ul>
          <p className={`mt-3 ${BODY_CLS}`}>
            South Beach is part of Miami Beach, while Brickell and Coconut Grove are neighborhoods within the City of Miami.
          </p>
        </section>

        <section className="mb-12" aria-labelledby="broward">
          <h2 id="broward" className={H2_CLS}>Broward County</h2>
          <p className={`mt-3 ${BODY_CLS}`}>
            Our coverage extends across coastal Broward, where we support condominium and community associations in:
          </p>
          <ul className={`mt-4 grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-2 ${BODY_CLS}`}>
            {BROWARD_CITIES.map((c) => (
              <li key={c} className="flex items-center gap-2">
                <span aria-hidden className="w-1 h-1 rounded-full bg-[#1D1D1F]" />
                {c}
              </li>
            ))}
          </ul>
        </section>

        <section className="mb-12" aria-labelledby="what-we-do">
          <h2 id="what-we-do" className={H2_CLS}>What we manage</h2>
          <p className={`mt-3 ${BODY_CLS}`}>
            Whether the community is a garden-style condominium, mid-rise condominium, high-rise condominium
            on the coast, or residential HOA community, PropHero delivers the same standard of service:
            transparent monthly financials, competitive vendor bidding, 24/7 resident and board communication,
            and proactive property management aligned with Florida statutes and CAM licensing requirements.
          </p>
        </section>
      </article>
      <SiteFooter crumbs={FOOTER_CRUMBS} />
    </main>
  );
}
